/**
 * The shared dashboard kit.
 *
 * Consumed by apps/landlord and apps/co-owner so both surfaces share one card
 * language, one table, one filter control and one set of empty/loading/error
 * treatments. Components here style themselves from the CSS variables in
 * ./tokens.css and never from an app-local palette — that is what lets the kit
 * drop into either app without inheriting whichever `--color-neutral-800` that
 * app happens to declare.
 *
 * Note: these use Tailwind utilities, which are only generated for apps whose
 * globals.css has an `@source` pointing at packages/ui/src. Landlord and
 * co-owner both do; check before consuming from a third app.
 */
// Named apart from the kit's other `Button`, which is a thin Mantine wrapper the
// non-dashboard apps use. Different design system, so both stay — same reasoning
// as HamburgerMenu vs DashboardNavDrawer.
export { DashboardButton, DashboardIconButton } from './Button'
export type { DashboardButtonProps, ButtonVariant, ButtonSize, ButtonTone } from './Button'

export { DashboardCard } from './DashboardCard'
export type { DashboardCardProps } from './DashboardCard'

export { DataTable } from './DataTable'
export type { DataTableColumn, DataTableProps } from './DataTable'

export { Pagination } from './Pagination'
export type { PaginationProps } from './Pagination'

export { FilterSelect } from './FilterSelect'
export type { FilterSelectOption } from './FilterSelect'

export { InfoHint } from './InfoHint'

export { SectionHeader } from './SectionHeader'
export type { SectionHeaderProps } from './SectionHeader'

export { StatCard, StatCardGrid } from './StatCard'
export type { StatCardProps } from './StatCard'

export { EmptyState, ErrorState, LoadingBlock, SkeletonCard, InlineEmpty } from './States'

export { RecordCard } from './RecordCard'
export type { RecordCardProps, RecordField } from './RecordCard'

export { ResponsiveTable } from './ResponsiveTable'
export type { ResponsiveTableProps } from './ResponsiveTable'

export { SegmentedTabs } from './SegmentedTabs'
export type { SegmentedTab, SegmentedTabsProps } from './SegmentedTabs'

export { ChartFrame, InfoStatCard, ChartLegend, ChartTooltip } from './ChartFrame'
export type { ChartFrameProps, InfoStat, LegendItem } from './ChartFrame'

export {
  CHART_GRID,
  CHART_AXIS,
  CHART_CURSOR,
  CHART_SERIES,
  CHART_MIN_HEIGHT,
} from './chartTheme'

export * from './shell'
